import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash, ListChecks, BellRinging } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { getApiErrorMessage } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple undated checklist - separate from the calendar Tasks. No client/date required,
// just a name + observations. Each item can optionally have its own one-time push
// reminder (date + time); every pending item is also included in a daily 09:00 digest
// push, sent automatically by the backend scheduler. Checking the box completes it and
// removes it from view.
export function TodoTab() {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchTodos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/todos`, { params: { status: "pendente" } });
      setTodos(res.data);
    } catch {
      toast.error("Erro ao carregar as Tarefas Pendentes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTodos(); }, []);

  const handleAdd = async () => {
    if (!title.trim()) {
      toast.error("Escreva o nome da tarefa");
      return;
    }
    if (!!reminderDate !== !!reminderTime) {
      toast.error("Indique data e hora do lembrete, ou nenhuma das duas");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/todos`, {
        title: title.trim(),
        notes: notes.trim() || null,
        reminder_date: reminderDate || null,
        reminder_time: reminderTime || null,
      });
      setTitle("");
      setNotes("");
      setReminderDate("");
      setReminderTime("");
      toast.success("Tarefa adicionada");
      fetchTodos();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao adicionar tarefa"));
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (id) => {
    setTodos((prev) => prev.filter((t) => t.id !== id));
    try {
      await axios.patch(`${API}/todos/${id}/complete`);
      toast.success("Tarefa concluída");
    } catch {
      toast.error("Erro ao concluir tarefa");
      fetchTodos();
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/todos/${id}`);
      toast.success("Tarefa eliminada");
      fetchTodos();
    } catch {
      toast.error("Erro ao eliminar tarefa");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div data-testid="todo-tab">
      <div className="gresart-card mb-4 space-y-3">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Nome da tarefa"
          data-testid="todo-title-input"
        />
        <Textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Observações (opcional)"
          data-testid="todo-notes-input"
        />
        <div>
          <Label className="text-xs text-stone-500">Notificação para esta tarefa (opcional)</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <Input type="date" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} data-testid="todo-reminder-date-input" />
            <Input type="time" value={reminderTime} onChange={(e) => setReminderTime(e.target.value)} data-testid="todo-reminder-time-input" />
          </div>
        </div>
        <Button className="btn-primary" onClick={handleAdd} disabled={saving} data-testid="btn-add-todo">
          <Plus size={16} className="mr-1.5" /> Adicionar
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-stone-400 text-center py-8">A carregar...</p>
      ) : todos.length === 0 ? (
        <div className="text-center py-12" data-testid="todo-empty">
          <ListChecks size={40} className="mx-auto text-stone-300 mb-3" />
          <p className="text-stone-500">Sem Tarefas Pendentes</p>
        </div>
      ) : (
        <div className="space-y-2">
          {todos.map((t) => (
            <div key={t.id} className="flex items-start gap-3 p-3 bg-white border border-stone-200 rounded-lg" data-testid={`todo-item-${t.id}`}>
              <Checkbox
                className="mt-1"
                onCheckedChange={() => handleComplete(t.id)}
                data-testid={`todo-checkbox-${t.id}`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-stone-400">
                  {new Date(t.created_at).toLocaleString("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
                <p className="text-sm font-medium text-stone-800">{t.title}</p>
                {t.notes && <p className="text-sm text-stone-500 mt-0.5">{t.notes}</p>}
                {t.reminder_date && t.reminder_time && (
                  <p className="text-xs text-[#009392] mt-1 flex items-center gap-1" data-testid={`todo-reminder-badge-${t.id}`}>
                    <BellRinging size={12} /> Lembrete: {t.reminder_date.split("-").reverse().join("/")} às {t.reminder_time}
                  </p>
                )}
              </div>
              <button
                onClick={() => setDeleteId(t.id)}
                className="p-1 text-red-400 hover:bg-red-50 rounded flex-shrink-0"
                data-testid={`btn-delete-todo-${t.id}`}
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!deleteId}
        onOpenChange={(v) => !v && setDeleteId(null)}
        onConfirm={() => handleDelete(deleteId)}
        itemType="tarefa"
      />
    </div>
  );
}
